// TemplatesVault.tsx
import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper as MuiPaper,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TimerIcon from "@mui/icons-material/Timer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import GlassContainer from "../../../components/liquid-glass";
import VaultGridCard from "./VaultGridCard";
import {
  Exercise,
  ExerciseTemplate,
  SetInfo,
  TemplateType,
  SchedulePlan,
} from "../../../../../models/types";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { exercisesList } from "../../exercise/exercise-list";
import { upsertTrainerTemplate } from "../../../../../redux/thunks/thunk-partners";
import { useEffect, useState } from "react";
import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { poolData } from "../groups";
import TemplateEditorDrawer from "../../../components/drawer-template-create";

type Props = {
  templates: TemplateType[];
  exercises: Exercise[];
  onSaveTemplate: (t: TemplateType) => void;
  onDeleteTemplate?: (id: string) => void;
  onCreateExercise: (e: Exercise) => void;
};

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const emptySet = (): SetInfo => ({
  id: uid(),
  previous: "—",
  current: { weight: 0, reps: 0 },
});
const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const ensureHttp = (u?: string | null) => {
  const v = (u ?? "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const cloneTemplate = (t: TemplateType): TemplateType =>
  JSON.parse(JSON.stringify(t));
const summarizeTemplate = (t: TemplateType) => {
  const totalSets = t.exercises.reduce((s, e) => s + (e.sets?.length || 0), 0);
  const totalExercises = t.exercises.length;
  return { totalSets, totalExercises };
};

type ExerciseOption = Exercise & { inputValue?: string; create?: boolean };
const filter = createFilterOptions<ExerciseOption>();

function dedupeExercises(
  primary: Exercise[],
  secondary: Exercise[]
): Exercise[] {
  const byId = new Map<string, Exercise>();
  const byName = new Map<string, Exercise>();

  const push = (e: Exercise) => {
    const idKey = (e.id ?? "").trim();
    const nameKey = (e.name ?? "").trim().toLowerCase();
    if (idKey) {
      if (!byId.has(idKey)) {
        byId.set(idKey, e);
        if (nameKey && !byName.has(nameKey)) byName.set(nameKey, e);
      }
      return;
    }
    if (nameKey && !byName.has(nameKey)) byName.set(nameKey, e);
  };

  primary.forEach(push);
  secondary.forEach((e) => {
    const idKey = (e.id ?? "").trim();
    const nameKey = (e.name ?? "").trim().toLowerCase();
    const dupById = idKey && byId.has(idKey);
    const dupByName = nameKey && byName.has(nameKey);
    if (dupById || dupByName) return;
    push(e);
  });

  const out: Exercise[] = [];
  const added = new Set<string>();
  for (const e of primary) {
    const key = (e.id ?? e.name ?? "") + "|" + (e.name ?? "");
    if (!added.has(key)) {
      out.push(e);
      added.add(key);
    }
  }
  for (const e of secondary) {
    const key = (e.id ?? e.name ?? "") + "|" + (e.name ?? "");
    if (!added.has(key)) {
      out.push(e);
      added.add(key);
    }
  }
  return out;
}

const hasAnyWeekday = (p?: SchedulePlan | null) =>
  !!p &&
  !!(
    p.weekdays.sunday ||
    p.weekdays.monday ||
    p.weekdays.tuesday ||
    p.weekdays.wednesday ||
    p.weekdays.thursday ||
    p.weekdays.friday ||
    p.weekdays.saturday
  );
const weekdayOrder: (keyof SchedulePlan["weekdays"])[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const weekdayShort: Record<keyof SchedulePlan["weekdays"], string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};
const formatPlannedUse = (p?: SchedulePlan | null) => {
  if (!p || !p.startDate) return "Planned: N/A";
  const isRecurring = hasAnyWeekday(p);
  const range =
    p.endDate && p.endDate !== p.startDate
      ? `${p.startDate} → ${p.endDate}`
      : p.startDate;
  if (!isRecurring) return range;
  const days = weekdayOrder
    .filter((k) => p.weekdays[k])
    .map((k) => weekdayShort[k]);
  return `${range} • ${days.join(", ") || "—"}`;
};

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

/** Get all muscles used by a single exercise */
const getExerciseMuscles = (ex: Exercise): string[] => {
  const muscles: string[] = [];
  const push = (val?: string | null) => {
    if (!val) return;
    const trimmed = val.trim();
    if (!trimmed) return;
    muscles.push(trimmed);
  };
  push(ex.muscleGroup);
  push(ex.targetMuscleGroup);
  push(ex.primeMoverMuscle);
  push(ex.secondaryMuscle);
  push(ex.tertiaryMuscle);
  if (Array.isArray(ex.detailedMusclesImpacted)) {
    ex.detailedMusclesImpacted.forEach((m) => push(m));
  }
  return muscles;
};

/** All unique muscles used in a template */
const getTemplateMuscles = (t: TemplateType): string[] => {
  const seen = new Map<string, string>();
  t.exercises.forEach((et) => {
    const ms = getExerciseMuscles(et.exercise || {});
    ms.forEach((m) => {
      const key = normalize(m);
      if (!seen.has(key)) seen.set(key, m);
    });
  });
  return Array.from(seen.values());
};

/** Palette for tag chips similar to meals/recipes chips, with dark/light variants */
const tagChipColors = (index: number, darkMode: boolean) => {
  const lightPalette = [
    { bg: "#EFF6FF", border: "#3B82F6", text: "#1D4ED8" }, // blue
    { bg: "#ECFEFF", border: "#06B6D4", text: "#0E7490" }, // cyan
    { bg: "#ECFDF3", border: "#22C55E", text: "#166534" }, // green
    { bg: "#FEFCE8", border: "#EAB308", text: "#854D0E" }, // yellow
    { bg: "#FEF2F2", border: "#F97373", text: "#B91C1C" }, // red
    { bg: "#F5F3FF", border: "#8B5CF6", text: "#5B21B6" }, // purple
    { bg: "#FFF7ED", border: "#FB923C", text: "#9A3412" }, // orange
  ];
  const darkPalette = [
    { bg: "#0B1220", border: "#60A5FA", text: "#E0F2FE" }, // blue
    { bg: "#042F2E", border: "#22D3EE", text: "#CCFBF1" }, // cyan
    { bg: "#022C22", border: "#34D399", text: "#DCFCE7" }, // green
    { bg: "#422006", border: "#FACC15", text: "#FEF9C3" }, // yellow
    { bg: "#450A0A", border: "#FB7185", text: "#FFE4E6" }, // red
    { bg: "#2E1065", border: "#A855F7", text: "#EDE9FE" }, // purple
    { bg: "#431407", border: "#FB923C", text: "#FFEDD5" }, // orange
  ];

  const palette = darkMode ? darkPalette : lightPalette;
  return palette[index % palette.length];
};

export default function TemplatesVault({
  templates,
  exercises,
  onSaveTemplate,
  onDeleteTemplate,
  onCreateExercise,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const darkMode = useSelector((state: RootState) => state.partners.darkMode);

  const textColor = darkMode ? "rgba(243,244,246,0.98)" : "rgba(15,23,42,0.98)";
  const subTextColor = darkMode
    ? "rgba(148,163,184,0.95)"
    : "rgba(55,65,81,0.92)";
  const borderColor = darkMode
    ? "rgba(148,163,184,0.35)"
    : "rgba(148,163,184,0.35)";

  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(true);
  const [idToken, setIdToken] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    setIsFetchingToken(true);
    const currentUser = new CognitoUserPool(poolData).getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err) {
          console.error("Error retrieving session:", err);
          setIsFetchingToken(false);
          return;
        }
        if (session && session.isValid()) {
          setIdToken(session.getIdToken().getJwtToken());
        }
        setIsFetchingToken(false);
      });
    } else {
      setIsFetchingToken(false);
    }
  }, []);

  const trainerUserId = useSelector(
    (s: RootState) =>
      s.partners.partnerData?.partnerData?.userId as string | undefined
  );

  const [panelOpen, setPanelOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<TemplateType | null>(null);
  const [mode, setMode] = React.useState<"VIEW" | "CREATE" | "EDIT">("VIEW");

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [newExercise, setNewExercise] = React.useState<Exercise>({
    id: null,
    name: "",
    type: null,
    muscleGroup: null,
    detailedMusclesImpacted: [],
    Instructions: [],
    shortYouTubeDemonstration: "",
    inDepthYouTubeExplanation: "",
  });
  const [pendingSetIndex, setPendingSetIndex] = React.useState<number | null>(
    null
  );

  const builtIns = React.useMemo<Exercise[]>(
    () => (Array.isArray(exercisesList) ? exercisesList : []),
    []
  );

  const combinedExercises = React.useMemo<Exercise[]>(
    () => dedupeExercises(exercises ?? [], builtIns),
    [exercises, builtIns]
  );

  const openWithTemplate = (t?: TemplateType) => {
    const base: TemplateType = t ?? {
      id: uid(),
      title: "New Template",
      exercises: [],
      dateLastUsed: "",
      plannedUse: undefined,
      difficulty: 3,
    };
    setDraft(cloneTemplate(base));
    setMode(t ? "EDIT" : "CREATE");
    setPanelOpen(true);
  };

  const setExerciseAt = (idx: number, ex: Partial<Exercise>) =>
    setDraft((d) =>
      !d
        ? d
        : {
          ...d,
          exercises: d.exercises.map((et, i) =>
            i === idx ? { ...et, exercise: { ...et.exercise, ...ex } } : et
          ),
        }
    );

  const confirmCreateExercise = () => {
    if (!newExercise.name?.trim()) return;
    const withId = {
      ...newExercise,
      id: newExercise.id ?? uid(),
      name: newExercise.name.trim(),
    };
    onCreateExercise(withId);
    if (draft && pendingSetIndex != null) {
      setExerciseAt(pendingSetIndex, {
        id: withId.id,
        name: withId.name,
        type: withId.type ?? null,
        muscleGroup: withId.muscleGroup ?? null,
        shortYouTubeDemonstration: withId.shortYouTubeDemonstration ?? "",
        inDepthYouTubeExplanation: withId.inDepthYouTubeExplanation ?? "",
      } as any);
    }
    setCreateDialogOpen(false);
    setPendingSetIndex(null);
  };

  const handleSave = async (t: TemplateType) => {
    onSaveTemplate(t); // optimistic update
    if (idToken && trainerUserId) {
      await dispatch(
        upsertTrainerTemplate({ idToken, trainerUserId, template: t })
      ).unwrap();
    }
  };

  const handleDelete = async (id: string) => {
    onDeleteTemplate?.(id);
    // sync to server if needed
  };

  // all template-level tags (and exercise-level tags) across templates
  const allTemplateTags = React.useMemo(() => {
    const set = new Map<string, string>();
    templates.forEach((t) => {
      const tags = (t as any).tags as string[] | undefined;
      if (Array.isArray(tags)) {
        tags.forEach((tag) => {
          const trimmed = (tag || "").trim();
          if (!trimmed) return;
          const key = normalize(trimmed);
          if (!set.has(key)) set.set(key, trimmed);
        });
      }
      // also collect tags on each exercise template if present
      t.exercises.forEach((et: any) => {
        const exTags = et?.tags as string[] | undefined;
        if (!Array.isArray(exTags)) return;
        exTags.forEach((tag) => {
          const trimmed = (tag || "").trim();
          if (!trimmed) return;
          const key = normalize(trimmed);
          if (!set.has(key)) set.set(key, trimmed);
        });
      });
    });
    return Array.from(set.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [templates]);

  const toggleTagFilter = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const filteredTemplates = React.useMemo(() => {
    const q = normalize(search.trim());
    const selectedTagKeys = new Set(
      selectedTags.map((t) => normalize(t.trim()))
    );

    return templates.filter((t) => {
      // tags to consider for filtering: template tags + exercise tags
      const templateTags = ((t as any).tags as string[]) || [];
      const exerciseTags: string[] = [];
      t.exercises.forEach((et: any) => {
        if (Array.isArray(et?.tags)) {
          exerciseTags.push(...et.tags);
        }
      });
      const allTagsForTemplate = [...templateTags, ...exerciseTags];

      if (selectedTagKeys.size > 0) {
        const hasMatch = allTagsForTemplate.some((tag) =>
          selectedTagKeys.has(normalize(tag))
        );
        if (!hasMatch) return false;
      }

      if (!q) return true;

      const { totalExercises, totalSets } = summarizeTemplate(t);
      const muscles = getTemplateMuscles(t);

      const haystack = [
        t.title,
        formatPlannedUse(t.plannedUse),
        t.dateLastUsed,
        `${totalExercises} exercises`,
        `${totalSets} sets`,
        ...allTagsForTemplate,
        ...t.exercises.map((et) => et.exercise?.name ?? ""),
        ...muscles,
      ]
        .filter(Boolean)
        .join(" ");

      return normalize(haystack).includes(q);
    });
  }, [templates, search, selectedTags]);

  return (
    <Box sx={{ color: textColor }}>
      {/* ── Unified toolbar ── */}
      <Stack
        direction="row"
        spacing={1.5}
        mb={2}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 1.5 }}
      >
        {/* Search */}
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          placeholder="Search templates…"
          label="Search"
          InputLabelProps={{
            sx: { color: `${textColor} !important`, fontSize: "0.8rem" },
          }}
          inputProps={{
            style: {
              fontSize: "0.85rem",
              color: textColor,
            },
          }}
          sx={{
            flex: 1,
            minWidth: 200,
            maxWidth: { xs: "100%", sm: "38%" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "transparent",
              "& fieldset": { borderColor: borderColor },
              "&:hover fieldset": {
                borderColor: darkMode
                  ? "rgba(248,250,252,0.8)"
                  : "rgba(15,23,42,0.9)",
              },
            },
          }}
        />

        {/* Tags dropdown (multi-select) */}
        {allTemplateTags.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel
              sx={{
                color: textColor,
                "&.Mui-focused": { color: textColor },
              }}
            >
              Tags
            </InputLabel>
            <Select
              multiple
              value={selectedTags}
              label="Tags"
              onChange={(e) => {
                const val = e.target.value;
                setSelectedTags(typeof val === "string" ? val.split(",") : val);
              }}
              renderValue={(sel) =>
                (sel as string[]).length === 0
                  ? "All"
                  : (sel as string[]).join(", ")
              }
              sx={{
                "& .MuiInputBase-input": { color: textColor },
                "& .MuiSvgIcon-root": { color: subTextColor },
              }}
            >
              {allTemplateTags.map((tag) => (
                <MenuItem key={tag} value={tag} sx={{ textTransform: "capitalize" }}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Spacer pushes view toggles + action to the right */}
        <Box sx={{ flex: "1 0 0", display: { xs: "none", md: "block" } }} />

        {/* View toggles */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {viewMode === "list" ? (
            <GlassContainer blur={18} radius={999} sx={{ p: 0.25 }}>
              <IconButton size="small" onClick={() => setViewMode("list")} color="primary" sx={{ color: textColor }}>
                <ViewListIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton size="small" onClick={() => setViewMode("list")} sx={{ opacity: 0.8, color: textColor }}>
              <ViewListIcon fontSize="small" />
            </IconButton>
          )}

          {viewMode === "grid" ? (
            <GlassContainer blur={18} radius={999} sx={{ p: 0.25 }}>
              <IconButton size="small" onClick={() => setViewMode("grid")} color="primary" sx={{ color: textColor }}>
                <ViewModuleIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton size="small" onClick={() => setViewMode("grid")} sx={{ opacity: 0.8, color: textColor }}>
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* + New template */}
        <GlassContainer blur={18} radius={999} sx={{ p: 0.4 }} dontHoverPop>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => openWithTemplate()}
            sx={{
              textTransform: "none",
              px: 1.5,
              py: 0.5,
              minWidth: 0,
              color: textColor,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            New template
          </Button>
        </GlassContainer>
      </Stack>

      {/* CONTENT: grid blocks OR table list */}
      {filteredTemplates.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ mt: 1, color: subTextColor, fontSize: "0.85rem" }}
        >
          No templates found. Try adjusting your search or filters.
        </Typography>
      ) : viewMode === "list" ? (
        <GlassContainer blur={18} radius={14} sx={{ mt: 0, p: 0.5 }}>
          <TableContainer
            component={MuiPaper}
            elevation={0}
            sx={{
              backgroundColor: "transparent",
            }}
          >
            <Table
              size="small"
              sx={{
                "& th": {
                  borderBottomColor: borderColor,
                  color: textColor,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                },
                "& td": {
                  borderBottomColor: borderColor,
                  color: textColor,
                  fontSize: "0.8rem",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Stats</TableCell>
                  <TableCell>Muscles</TableCell>
                  <TableCell>Planned use</TableCell>
                  <TableCell>Last used</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.map((t) => {
                  const { totalExercises, totalSets } = summarizeTemplate(t);
                  const muscles = getTemplateMuscles(t);
                  return (
                    <TableRow
                      key={t.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: darkMode
                            ? "rgba(15,23,42,0.7)"
                            : "rgba(248,250,252,0.9)",
                        },
                      }}
                      onClick={() => openWithTemplate(t)}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{t.title}</TableCell>
                      <TableCell>
                        {totalExercises} ex • {totalSets} sets
                      </TableCell>
                      <TableCell>
                        {muscles.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {muscles.slice(0, 4).map((m) => (
                              <Chip
                                key={m}
                                size="small"
                                label={m}
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 22,
                                  color: textColor,
                                  borderColor: borderColor,
                                  backgroundColor: darkMode
                                    ? "rgba(15,23,42,0.8)"
                                    : "rgba(248,250,252,0.9)",
                                }}
                                variant="outlined"
                              />
                            ))}
                            {muscles.length > 4 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  ml: 0.5,
                                  opacity: 0.7,
                                  color: subTextColor,
                                }}
                              >
                                +{muscles.length - 4} more
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.6, color: subTextColor }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatPlannedUse(t.plannedUse)}</TableCell>
                      <TableCell>
                        {t.dateLastUsed
                          ? new Date(t.dateLastUsed).toLocaleString()
                          : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassContainer>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 1.25,
            py: 1,
            px: 0.5,
          }}
        >
          {filteredTemplates.map((t) => {
            const { totalExercises, totalSets } = summarizeTemplate(t);
            return (
              <VaultGridCard
                key={t.id}
                onClick={() => openWithTemplate(t)}
                minHeight={176}
                sx={{ color: textColor }}
                title={t.title || "Untitled template"}
                chips={
                  <>
                    <Chip
                      size="small"
                      icon={
                        <FitnessCenterIcon
                          sx={{ fontSize: "1rem", color: textColor }}
                        />
                      }
                      label={`${totalExercises} ex • ${totalSets} sets`}
                      variant="outlined"
                      sx={{
                        borderColor: borderColor,
                        color: textColor,
                        fontSize: "0.72rem",
                        height: 24,
                        "& .MuiChip-icon": {
                          color: textColor,
                        },
                      }}
                    />
                    <Chip
                      size="small"
                      icon={
                        <CalendarMonthIcon
                          sx={{ fontSize: "1rem", color: textColor }}
                        />
                      }
                      label={formatPlannedUse(t.plannedUse)}
                      variant="outlined"
                      sx={{
                        borderColor: borderColor,
                        color: textColor,
                        fontSize: "0.72rem",
                        height: 24,
                        "& .MuiChip-icon": {
                          color: textColor,
                        },
                      }}
                    />
                  </>
                }
                footer={
                  <Typography
                    variant="caption"
                    sx={{
                      color: subTextColor,
                      fontSize: "0.7rem",
                    }}
                  >
                    Last used:{" "}
                    {t.dateLastUsed ? new Date(t.dateLastUsed).toLocaleString() : "—"}
                  </Typography>
                }
              />
            );
          })}
        </Box>
      )}

      <TemplateEditorDrawer
        open={panelOpen}
        anchor="right"
        draft={draft ?? undefined}
        onChange={(t) => setDraft(t)}
        exercises={combinedExercises}
        darkMode={darkMode}
        onSave={handleSave}
        onDelete={onDeleteTemplate ? (id) => handleDelete(id) : undefined}
        onClose={() => setPanelOpen(false)}
        availableTags={allTemplateTags}
      />

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            color: darkMode ? "#F9FAFB" : "#111827",
            backgroundColor: darkMode ? "#020617" : "#FFFFFF",
          }}
        >
          Create Exercise
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: darkMode ? "#020617" : "#FFFFFF",
          }}
        >
          <Stack spacing={1} mt={1}>
            <TextField
              autoFocus
              label="Exercise name *"
              value={newExercise.name ?? ""}
              onChange={(e) =>
                setNewExercise((x) => ({
                  ...(x as Exercise),
                  name: e.target.value,
                }))
              }
              size="small"
              InputLabelProps={{
                sx: {
                  color: `${darkMode ? "#E5E7EB" : "#111827"} !important`,
                  fontSize: "0.8rem",
                },
              }}
              inputProps={{
                style: {
                  color: darkMode ? "#F9FAFB" : "#111827",
                  fontSize: "0.85rem",
                },
              }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Type"
                value={newExercise.type ?? ""}
                onChange={(e) =>
                  setNewExercise((x) => ({
                    ...(x as Exercise),
                    type: e.target.value,
                  }))
                }
                size="small"
                select
                sx={{ minWidth: 160 }}
                InputLabelProps={{
                  sx: {
                    color: `${darkMode ? "#E5E7EB" : "#111827"} !important`,
                    fontSize: "0.8rem",
                  },
                }}
                inputProps={{
                  style: {
                    color: darkMode ? "#F9FAFB" : "#111827",
                    fontSize: "0.85rem",
                  },
                }}
              >
                {[
                  "barbell",
                  "dumbbell",
                  "machine",
                  "bodyweight",
                  "band",
                  "cable",
                  "other",
                ].map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Muscle group"
                value={newExercise.muscleGroup ?? ""}
                onChange={(e) =>
                  setNewExercise((x) => ({
                    ...(x as Exercise),
                    muscleGroup: e.target.value,
                  }))
                }
                size="small"
                select
                sx={{ minWidth: 160 }}
                InputLabelProps={{
                  sx: {
                    color: `${darkMode ? "#E5E7EB" : "#111827"} !important`,
                    fontSize: "0.8rem",
                  },
                }}
                inputProps={{
                  style: {
                    color: darkMode ? "#F9FAFB" : "#111827",
                    fontSize: "0.85rem",
                  },
                }}
              >
                {[
                  "chest",
                  "back",
                  "shoulders",
                  "biceps",
                  "triceps",
                  "legs",
                  "glutes",
                  "core",
                  "full body",
                  "other",
                ].map((mg) => (
                  <MenuItem key={mg} value={mg}>
                    {mg}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Short demo (YouTube URL)"
              value={newExercise.shortYouTubeDemonstration ?? ""}
              onChange={(e) =>
                setNewExercise((x) => ({
                  ...(x as Exercise),
                  shortYouTubeDemonstration: e.target.value,
                }))
              }
              size="small"
              InputLabelProps={{
                sx: {
                  color: `${darkMode ? "#E5E7EB" : "#111827"} !important`,
                  fontSize: "0.8rem",
                },
              }}
              inputProps={{
                style: {
                  color: darkMode ? "#F9FAFB" : "#111827",
                  fontSize: "0.85rem",
                },
              }}
            />
            <TextField
              label="In-depth demo (YouTube URL)"
              value={newExercise.inDepthYouTubeExplanation ?? ""}
              onChange={(e) =>
                setNewExercise((x) => ({
                  ...(x as Exercise),
                  inDepthYouTubeExplanation: e.target.value,
                }))
              }
              size="small"
              InputLabelProps={{
                sx: {
                  color: `${darkMode ? "#E5E7EB" : "#111827"} !important`,
                  fontSize: "0.8rem",
                },
              }}
              inputProps={{
                style: {
                  color: darkMode ? "#F9FAFB" : "#111827",
                  fontSize: "0.85rem",
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#020617" : "#FFFFFF",
          }}
        >
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              textTransform: "none",
              color: darkMode ? "#E5E7EB" : "#111827",
              fontSize: "0.85rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmCreateExercise}
            disabled={!newExercise.name?.trim()}
            sx={{
              textTransform: "none",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
